import React, { useCallback, useEffect, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Badge from "components/common/Badge";
import { config } from "config";
import { intercom } from "library/tracker/intercom";
import { pushNotification } from "library/pushNotification";
import {
  HEADER_ICON_BUTTON_SX,
  NAV_COLORS,
} from "components/allInOneDashboard/constants/navTheme";

const HeaderIconButton = ({ onClick, children, ariaLabel, disabled }) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    sx={{
      ...HEADER_ICON_BUTTON_SX,
      p: 0,
      m: 0,
      font: "inherit",
      color: "inherit",
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      "&:hover": disabled ? undefined : { bgcolor: "#F9FAFB" },
    }}
  >
    {children}
  </Box>
);

const HeaderActions = ({
  onNotificationClick,
  onProfileClick,
  onMenuClick,
  onLogoutClick,
  isProfileDisabled = false,
  unreadCount = 0,
}) => {
  const isNativeApp = config.isMobileApp;
  const [pushGranted, setPushGranted] = useState(false);

  const refreshPushPermission = useCallback(async () => {
    if (!isNativeApp) return;
    try {
      const granted = await pushNotification.isPermissionGranted();
      setPushGranted(Boolean(granted));
    } catch {
      setPushGranted(false);
    }
  }, [isNativeApp]);

  useEffect(() => {
    refreshPushPermission();
  }, [refreshPushPermission]);

  const handlePushSettingsClick = async () => {
    await pushNotification.openNotificationSettings();
    // Re-check after user may have toggled settings.
    window.setTimeout(refreshPushPermission, 800);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={1}
      sx={{ flexShrink: 0 }}
    >
      {/* Mobile-web: open side menu (hidden on desktop + native apps) */}
      {!isNativeApp ? (
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <HeaderIconButton ariaLabel="Open menu" onClick={onMenuClick}>
            <MenuIcon sx={{ fontSize: 24, color: NAV_COLORS.nseText, zIndex: 1 }} />
          </HeaderIconButton>
        </Box>
      ) : null}

      {/* Native: OS push notification settings */}
      {/* {isNativeApp ? (
        <HeaderIconButton
          ariaLabel="Notification settings"
          onClick={handlePushSettingsClick}
        >
          {pushGranted ? (
            <NotificationsActiveIcon
              sx={{ fontSize: 22, color: "#2B70C5", zIndex: 1 }}
            />
          ) : (
            <NotificationsOffIcon
              sx={{ fontSize: 22, color: "#9AA0A7", zIndex: 1 }}
            />
          )}
        </HeaderIconButton>
      ) : null} */}

      <HeaderIconButton
        ariaLabel="Notifications"
        onClick={onNotificationClick}
      >
        <Badge
          overlap="circular"
          color="primary"
          variant="dot"
          invisible={!unreadCount}
          sx={{
            zIndex: 1,
            "& .MuiBadge-badge": { bgcolor: "#FF0000" },
          }}
        >
          <NotificationsNoneOutlinedIcon
            sx={{ fontSize: 24, color: NAV_COLORS.nseText }}
          />
        </Badge>
      </HeaderIconButton>

      {/* Native: profile shortcut (not in bottom nav). Mobile-web uses sidebar. */}
      {isNativeApp ? (
        <HeaderIconButton
          ariaLabel="Profile"
          onClick={onProfileClick}
          disabled={isProfileDisabled}
        >
          <PersonOutlineOutlinedIcon
            sx={{ fontSize: 24, color: NAV_COLORS.nseText, zIndex: 1 }}
          />
        </HeaderIconButton>
      ) : null}

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <HeaderIconButton
          ariaLabel="Support chat"
          onClick={() => intercom.show()}
        >
          <HeadsetMicOutlinedIcon
            sx={{ fontSize: 24, color: NAV_COLORS.nseText, zIndex: 1 }}
          />
        </HeaderIconButton>
      </Box>

      {onLogoutClick ? (
        <HeaderIconButton ariaLabel="Logout" onClick={onLogoutClick}>
          <LogoutOutlinedIcon
            sx={{ fontSize: 24, color: NAV_COLORS.nseText, zIndex: 1 }}
          />
        </HeaderIconButton>
      ) : null}
    </Stack>
  );
};

export default HeaderActions;
